"use client";

import { useTranslations } from "@frontend/lib/next-intl-typed";
import { AnimatePresence, motion } from "framer-motion";
import type { Variants } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import { Suspense, useLayoutEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import type { Client } from "./MarketHome";

export const ExpandButton = ({
  text,
  mode = "light",
  href,
  asGhost = false,
}: {
  text: string;
  mode?: "light" | "dark";
  href?: string;
  asGhost?: boolean;
}) => {
  const MotionEl = href ? motion.a : motion.div;
  const baseClasses =
    "flex items-center gap-2 text-sm justify-between rounded-full px-6 py-3 transition delay-150 duration-300 ease-in-out";

  let colorClasses = "";
  if (asGhost) {
    colorClasses =
      mode === "dark"
        ? "bg-black text-white border-2 border-white"
        : "border-2 border-black text-black";
  } else {
    colorClasses =
      mode === "dark"
        ? "bg-white border-2 border-black text-black"
        : "bg-black text-white";
  }

  const isWhiteText =
    (!asGhost && mode === "light") || (asGhost && mode === "dark");

  const buttonRef = useRef<HTMLAnchorElement | HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useLayoutEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
  }, []);

  return (
    <MotionEl
      {...(href ? { href } : {})}
      ref={(node: HTMLDivElement | HTMLAnchorElement | null) => {
        buttonRef.current = node;
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      animate={{ width: width ? (isHovered ? width * 1.1 : width) : undefined }}
      transition={{ width: { duration: 0.3, ease: "easeOut" } }}
      whileTap={{ scale: 0.95 }}
      className={`${baseClasses} ${colorClasses}`}
      style={{ width: width ?? undefined }}
    >
      <span className={isWhiteText ? "text-white" : "text-black"}>{text}</span>
      <ArrowRightIcon color={isWhiteText ? "white" : "black"} />
    </MotionEl>
  );
};

// Staggered container and card variants for entrance/exit animations
const containerVariants: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0,
      staggerChildren: 0.15,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
};

export const ClientCard = ({
  baseShopUrl,
  client,
  variants,
}: { baseShopUrl: string; client: Client; variants?: Variants }) => {
  const t = useTranslations("Shop");
  const tags = [
    ...(client.configFlags?.["Insurance Intake"] ? [t("InsuredShopping")] : []),
    ...(client.configFlags?.Booking ? [t("BookingAvailable")] : []),
  ];

  return (
    <motion.a
      className="rounded-3xl bg-white overflow-hidden min-h-[540px] flex flex-col"
      style={{ boxShadow: "0px 6px 16px 0px rgba(0, 0, 0, 0.08)" }}
      href={`${baseShopUrl}/${client.org.orgTitle.toLowerCase()}`}
      variants={variants}
      whileHover={{
        scale: 1.01,
        boxShadow: "0px 20px 40px 0px rgba(0, 0, 0, 0.4)",
        transition: {
          scale: { duration: 0.3, ease: "easeOut" },
          boxShadow: { duration: 0.65, ease: "easeOut" },
        },
      }}
      whileTap={{
        scale: 0.99,
        transition: { duration: 0.2, ease: "easeInOut" },
      }}
    >
      <div className="flex-1 flex flex-col">
        <div className="relative w-full h-64">
          <Image
            className="object-cover object-center"
            src={
              client.seoImageUrl ??
              "https://rsup2.b-cdn.net/placeholder_image.png"
            }
            alt={client.org.orgName ?? ""}
            fill
            quality={100}
            sizes="(min-width: 1024px) 300px, 100vw"
          />
        </div>
        <div className="relative flex flex-col items-start justify-between flex-1 gap-2 mx-4">
          <div className="absolute -top-10">
            <div className="flex items-center rounded-full aspect-square w-16 h-16 border border-gray-200 shadow-md bg-white">
              <Image
                src={client.lightModeLogo ?? client.darkModeLogo ?? ""}
                alt={client.org.orgName ?? ""}
                fill
                className="object-contain p-2"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col items-start gap-2">
            <h2 className="text-xl font-bold">{client.org.orgName}</h2>
            <p className="text-sm text-gray-500 leading-snug">
              {client.seoDescription}
            </p>
          </div>

          <div className="mt-4 flex flex-col items-start mb-4 gap-4">
            <div>
              {/* Booking and shop with insurance tags */}
              <div className="flex flex-row items-start gap-2">
                {tags.map((tag) => (
                  <div
                    key={tag}
                    className="border-gray-300 border rounded-lg px-2 py-1 text-xs text-gray-500"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>
            <ExpandButton text={t("ShopNow")} mode="light" asGhost={false} />
          </div>
        </div>
      </div>
    </motion.a>
  );
};

export const ClientGrid = ({
  richClients,
  baseShopUrl,
}: { richClients: Client[]; baseShopUrl: string }) => {
  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        className="w-full grid gap-8 mb-32 p-2 md:p-10"
        style={{
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
        }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        {richClients.map((client) => (
          <ClientCard
            key={client.org.orgTitle.toLowerCase()}
            baseShopUrl={baseShopUrl}
            client={client as Client}
            variants={cardVariants}
          />
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export const HeroSection = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const t = useTranslations("Shop");

  return (
    <Suspense fallback={null}>
      {isMobile ? (
        <div className="w-full flex flex-col items-center justify-center h-64">
          <h1 className="text-4xl font-bold text-center max-w-3/4">
            {t("ShopForHealthcareMobile")}
          </h1>
          <p className="text-lg leading-tight text-center mt-4 max-w-3/4">
            {t("WelcomeToSuperscriptMobile")}
          </p>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center justify-center h-fit my-12 gap-4">
          <h1 className="text-4xl md:text-6xl font-bold text-center max-w-5/6 md:max-w-2xl">
            {t("ShopForHealthcareDesktop")}
          </h1>
          <p className="text-lg md:text-2xl leading-tight text-center mt-4 max-w-5/6 md:max-w-2xl">
            {t("WelcomeToSuperscriptDesktop")}
          </p>
        </div>
      )}
    </Suspense>
  );
};

export const Footer = ({
  homePageData,
}: {
  homePageData: {
    navLogoUrl: string;
    navLogoAltText: string;
    navContactUrl: string;
    dotsWaveAssetUrl: string;
    poweredBySuperscriptImageUrl: string;
  };
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const t = useTranslations("Shop");
  return (
    <Suspense fallback={null}>
      {isMobile ? (
        <div className="w-full flex flex-col gap-8 px-0 pt-12 pb-0 bg-black">
          <div className="flex flex-col items-center justify-center gap-4">
            <h2 className="text-4xl text-white font-bold text-center max-w-3/4">
              {t("WantToKnowHowMobile")}
            </h2>
            <p className="text-gray-300 text-md mt-2 mb-8 text-center max-w-3/4">
              {t("CuriousAboutHowMobile")}
            </p>
            <ExpandButton
              href={homePageData.navContactUrl}
              mode="dark"
              asGhost={true}
              text="Become a partner"
            />
          </div>
          <div className="flex flex-col items-center justify-center gap-4">
            <Image
              src={homePageData.poweredBySuperscriptImageUrl}
              width={200}
              height={200}
              alt="Powered by Superscript"
            />
          </div>
          <div className="relative w-full h-64">
            <Image
              src={homePageData.dotsWaveAssetUrl}
              alt="Dots Wave"
              fill
              sizes="100vw"
              className="object-cover object-center"
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[600px] flex flex-col items-start px-0 md:px-12 xl:px-40 pt-24 pb-0 md:pb-24 bg-black relative">
          {/* Dots Image graphic (lowest layer) */}
          <div className="absolute bottom-0 top-0 right-0 left-0 pointer-events-none bg-gradient-to-r from-black via-black/70 lg:via-black/90 to-transparent">
            <Image
              src={homePageData.dotsWaveAssetUrl}
              alt="Dots Wave"
              fill
              sizes="75vw"
              className="object-contain object-right-bottom"
            />
          </div>

          {/* Background gradient (middle layer) */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 lg:via-black/90 to-transparent pointer-events-none" />

          {/* Text content (highest layer) */}
          <div className="relative z-10 flex flex-col px-4 md:px-0 justify-between h-full">
            <div className="flex flex-col md:items-start flex-1">
              <h2 className="text-5xl lg:text-6xl text-white font-bold text-center md:text-left max-w-full md:max-w-[70%]">
                {t("WantToKnowHowDesktop")}
              </h2>
              <p className="text-gray-300 text-md mt-2 mb-8 text-center md:text-left max-w-full md:max-w-1/2 xl:max-w-1/3">
                {t("TransformHealthcare")}
              </p>
              <ExpandButton
                href={homePageData.navContactUrl}
                text={t("BecomeAPartner")}
                mode="dark"
                asGhost={true}
              />
            </div>
            <div className="">
              <Image
                src={homePageData.poweredBySuperscriptImageUrl}
                width={200}
                height={200}
                alt="Powered by Superscript"
              />
            </div>
          </div>
        </div>
      )}
    </Suspense>
  );
};
